// packages/frontend/src/services/pendingProfile.ts

import type { User } from "@msb/shared";
import { createLogger } from "../utils/logger";
import { billingService } from "./billingService";
import { firebaseFunctions } from "./firebaseFunctions";

// Registration data that cannot be saved until the email is verified. The names
// entered at registration are held here and applied once the first verified
// load happens. A beta code from the invite link is held the same way, as a
// fallback for when the immediate activation call never succeeds.
//
// Stored in localStorage (not sessionStorage) because the verification link is
// usually opened in another tab. Keyed by Firebase Auth UID. These are therapist
// names, not client PHI, and the values are never logged. The beta code is an
// invite code, not PII, and is never logged either.

export interface PendingProfile {
  prefix?: string;
  firstName?: string;
  lastName?: string;
  betaCode?: string;
}

interface StoredPendingProfile extends PendingProfile {
  createdAt: number;
}

const KEY_PREFIX = "msb.pendingProfile.";
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Delays between attempts to activate a beta code right after registration.
// The applyBetaCode callable needs the user document, which the onUserCreated
// trigger writes asynchronously, so an early call can fail with
// failed-precondition. Total wait is 7.5 s across four retries.
export const BETA_RETRY_DELAYS_MS: readonly number[] = [500, 1000, 2000, 4000];

const FAILED_PRECONDITION = "functions/failed-precondition";
const INVALID_ARGUMENT = "functions/invalid-argument";

const logger = createLogger("PendingProfile");
const inFlight = new Set<string>();
const betaInFlight = new Set<string>();

const keyFor = (uid: string): string => `${KEY_PREFIX}${uid}`;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

function callableErrorCode(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("code" in error)) {
    return undefined;
  }
  const { code } = error as { code: unknown };
  return typeof code === "string" ? code : undefined;
}

function readStored(uid: string): StoredPendingProfile | null {
  try {
    const raw = localStorage.getItem(keyFor(uid));
    if (!raw) return null;

    const stored = JSON.parse(raw) as Partial<StoredPendingProfile>;
    const valid =
      typeof stored.createdAt === "number" &&
      (stored.prefix === undefined || typeof stored.prefix === "string") &&
      (stored.firstName === undefined || typeof stored.firstName === "string") &&
      (stored.lastName === undefined || typeof stored.lastName === "string") &&
      (stored.betaCode === undefined || typeof stored.betaCode === "string");

    if (!valid || Date.now() - stored.createdAt! > TTL_MS) {
      clearPendingProfile(uid);
      return null;
    }

    return stored as StoredPendingProfile;
  } catch {
    clearPendingProfile(uid);
    return null;
  }
}

function writeStored(uid: string, stored: StoredPendingProfile): void {
  localStorage.setItem(keyFor(uid), JSON.stringify(stored));
}

export function savePendingProfile(uid: string, profile: PendingProfile): void {
  try {
    writeStored(uid, {
      prefix: profile.prefix,
      firstName: profile.firstName,
      lastName: profile.lastName,
      createdAt: Date.now(),
    });
  } catch {
    logger.warn("Could not store pending registration profile", {
      firebaseUid: uid,
    });
  }
}

export function loadPendingProfile(uid: string): PendingProfile | null {
  const stored = readStored(uid);
  if (!stored) return null;

  return {
    prefix: stored.prefix,
    firstName: stored.firstName,
    lastName: stored.lastName,
    betaCode: stored.betaCode,
  };
}

export function clearPendingProfile(uid: string): void {
  try {
    localStorage.removeItem(keyFor(uid));
  } catch {
    // Storage unavailable: nothing was stored to clear.
  }
}

/**
 * Holds a beta code for the account until the first verified load. Keeps any
 * names already held for the same UID and the original expiry.
 */
export function savePendingBetaCode(uid: string, betaCode: string): void {
  try {
    const existing = readStored(uid);
    writeStored(uid, {
      ...existing,
      createdAt: existing?.createdAt ?? Date.now(),
      betaCode,
    });
  } catch {
    logger.warn("Could not store pending beta code", {
      firebaseUid: uid,
    });
  }
}

/** Removes only the held beta code. Names held for the same UID stay. */
export function clearPendingBetaCode(uid: string): void {
  const stored = readStored(uid);
  if (!stored?.betaCode) return;

  if (stored.firstName === undefined) {
    clearPendingProfile(uid);
    return;
  }

  try {
    writeStored(uid, {
      prefix: stored.prefix,
      firstName: stored.firstName,
      lastName: stored.lastName,
      createdAt: stored.createdAt,
    });
  } catch {
    // Storage unavailable: the held code could not have been stored either.
  }
}

/**
 * Activates a beta code right after registration, in the background. Returns
 * at once so navigation is never blocked. The code is held before the first
 * call, so a page closed mid-retry still leaves it for the first verified load.
 * Only failed-precondition is retried; a definitive invalid-argument clears the
 * held code. `onApplied` runs after the server accepts the code.
 */
export function startBetaActivation(
  uid: string,
  betaCode: string,
  onApplied?: () => void,
): void {
  if (betaInFlight.has(uid)) return;

  savePendingBetaCode(uid, betaCode);
  betaInFlight.add(uid);
  void retryBetaActivation(uid, betaCode, onApplied).finally(() => {
    betaInFlight.delete(uid);
  });
}

async function retryBetaActivation(
  uid: string,
  betaCode: string,
  onApplied?: () => void,
): Promise<void> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await billingService.applyBetaCode(betaCode);
      clearPendingBetaCode(uid);
      logger.info("Beta code applied after registration", {
        firebaseUid: uid,
        attempts: attempt + 1,
      });
      onApplied?.();
      return;
    } catch (error) {
      const code = callableErrorCode(error);

      if (code === INVALID_ARGUMENT) {
        clearPendingBetaCode(uid);
        logger.warn("Beta code rejected by the server; not retried", {
          firebaseUid: uid,
        });
        return;
      }

      if (code !== FAILED_PRECONDITION || attempt >= BETA_RETRY_DELAYS_MS.length) {
        logger.warn("Beta code not applied after registration; held for the first verified load", {
          firebaseUid: uid,
          errorCode: code,
        });
        return;
      }

      await sleep(BETA_RETRY_DELAYS_MS[attempt]);
    }
  }
}

/**
 * Applies the held names and beta code, once each. Call only for a verified
 * account. Never throws: a step that fails keeps the held data for the next
 * verified load and the user is returned unchanged, so login is not blocked.
 */
export async function applyPendingProfile(uid: string, user: User): Promise<User> {
  if (inFlight.has(uid)) return user;

  const pending = loadPendingProfile(uid);
  if (!pending) return user;

  inFlight.add(uid);
  try {
    const names = await applyHeldNames(uid, pending, user);
    const betaDone = await applyHeldBetaCode(uid, pending, user);
    if (names.done && betaDone) {
      clearPendingProfile(uid);
    }
    return names.user;
  } finally {
    inFlight.delete(uid);
  }
}

async function applyHeldNames(
  uid: string,
  pending: PendingProfile,
  user: User,
): Promise<{ user: User; done: boolean }> {
  const { prefix, firstName, lastName } = pending;
  if (firstName === undefined || lastName === undefined) {
    return { user, done: true };
  }

  const alreadySaved =
    user.firstName === firstName &&
    (user.lastName ?? "") === lastName &&
    (user.prefix ?? "") === (prefix ?? "");
  if (alreadySaved) {
    return { user, done: true };
  }

  try {
    await firebaseFunctions.updateUser({
      // The document is keyed by the Firebase Auth UID, the same value
      // updateUser checks against request.auth.uid.
      userId: uid,
      data: {
        profile: {
          prefix: prefix || undefined,
          firstName,
          lastName,
          displayName: `${firstName} ${lastName}`.trim(),
        },
      },
    });
    logger.info("Applied pending registration profile", { firebaseUid: uid });
    return { user: { ...user, prefix, firstName, lastName }, done: true };
  } catch (error) {
    logger.warn("Failed to apply pending registration profile; will retry on next verified load", {
      firebaseUid: uid,
      error: error instanceof Error ? error.message : "Unknown error",
    });
    return { user, done: false };
  }
}

async function applyHeldBetaCode(
  uid: string,
  pending: PendingProfile,
  user: User,
): Promise<boolean> {
  if (!pending.betaCode) return true;

  // Only the free plan takes a beta code. A user already on a paid plan keeps it.
  if (user.planType && user.planType !== "free") return true;

  try {
    await billingService.applyBetaCode(pending.betaCode);
    logger.info("Applied held beta code after verification", { firebaseUid: uid });
    return true;
  } catch (error) {
    if (callableErrorCode(error) === INVALID_ARGUMENT) {
      logger.warn("Held beta code rejected by the server; discarded", { firebaseUid: uid });
      return true;
    }
    logger.warn("Failed to apply held beta code; will retry on next verified load", {
      firebaseUid: uid,
      errorCode: callableErrorCode(error),
    });
    return false;
  }
}
